import { useAnalytics } from '@ecosystem/newron-client-lib'
import store from 'reducers/store'
import { Location } from 'typings/filters'
import { CIE_ALLOWED_PATH } from 'util/urlUtils'

interface NavClickMessage {
  linkName: string
  pageName: string
  section: string
  subsection?: string
  navigationSource?: string
}

interface FiltersApplied {
  access: string
  categoryCombination: string
  filterName: string
  optionChosen: string[]
  pageName: string
  subsection: string
  section: string
}

interface FiltersAppliedDetails {
  access: string
  categoryChosen: string
  categoryCombination: string
  filterName: string
  optionChosen: string[]
  pageName: string
  subsection?: string
  section: string
}

interface ChartInteractions {
  access: string
  chartName: string
  dataSource: string
  elementName: string
  elementType: string
  optionChosen?: string | string[]
  pageName: string
  subsection?: string
  section: string
}

interface SearchClicked {
  access: string
  actionType: string
  keyword: string
  keywordOrigin: string
  pageName: string
  subsection: string
  section: string
}

interface FilterTrayButtonClick {
  page: string
  action: string
  buttonName: string
}

interface PageElementClick {
  access: string
  elementCategory: string
  elementName: string
  pageName: string
  subsection: string
  section: string
  optionChosen?: string[]
}

interface TableClicked {
  access: string
  column?: string
  elementName: string
  elementType?: string
  optionChosen?: string | string[]
  pageName: string
  subsection?: string
  section: string
  tableName?: string
}
interface PageElementViewed {
  access: string
  elementCategory: string
  elementName: string
  pageName: string
  subsection: string
  section: string
  dataAvailability: string
}

interface PageScrolled {
  // access: string
  deepestChartViewed?: string
  referrer: string
  scroll?: number
}

interface PageViewed {
  pageName: string
  subsection: string
  section: string
}

export enum AnalyticsEvent {
  NavigationClick = 'Navigation Clicked',
  FiltersApplied = 'Filter Applied',
  FiltersAppliedDetails = 'Filter Applied Details',
  SearchClicked = 'Search Clicked',
  ChartInteractions = 'Chart Interactions',
  PageElementClick = 'Page Element Clicked',
  PageElementViewed = 'Page Element Viewed',
  PageScrolled = 'Page Scrolled',
  PageViewed = 'Page Viewed',
  TableClicked = 'Table Clicked',
  DataExported = 'Data Exported',
  DataExportInitiated = 'Data Export Initiated',
}

export enum TRACKING {
  ACCESS = 'consumer',
  FILTER_NAME = 'Consumer Filter',
  ELEMENT_NAME = 'View By',
  ELEMENT_TYPE = 'Dropdown',
  DATA_SOURCE = 'Survey',
  HINT = 'Hint',
  REVIEW_DATA_SOURCE = 'Consumer Reviews',
  FILTER_BAR = 'Filter Bar',
  CLEAR_FILTERS = 'Clear Filters',
  EXPAND_FILTER_BAR = 'Expand Filter Bar',
  EXPORT = 'Export',
  SAVE_FILTER = 'Save Filter',
  SAMPLE_SIZE_LABEL = 'Low sample size label',
  LOW_SAMPLE_SIZE = 'Low sample size',
  YES = 'yes',
  NO = 'no (low sample size)',
  YES_LOW_SAMPLE_SIZE = 'yes (low sample size)',
  NO_DATA = 'no (no data)',
  LEGEND = 'Legend',
  CHART_ENGAGEMENT = 'Chart Engagement',
  FILTER_TRAY = 'Filter Tray',
  EXPAND_FILTER_TRAY = 'Expand Filter Tray',
  SEARCH_CLICKED = 'Search clicked(keyword entered)',
  MANUAL_ENTRY = 'Manual Entry',
  DROPDOWN = 'Dropdown',
  CONTENT_LINK = 'Content Link',
  LINK = 'link',
  Chart = 'chart',
  JUMP_POINT = 'Jump Point',
  Chart_Tooltip = 'Chart Tooltip',
  SELECT_AUDIENCES = 'Select Audiences',
  AUDIENCE_SELECTOR = 'Audience Selector',
  COMPARE_BY = 'Compare By',
  TABLE_OVERLAY = 'Table Overlay',
  OVERLAY = 'Overlay',
  BUBBLE_OVERLAY = 'Bubble Overlay',
  SORT = 'Sort',
  TRELLIS_SORT = 'Trellis Sort',
  YES_NO_DATA = 'yes (no data)',
  YES_LOW_SAMPLE_NO_DATA = 'yes(low sample size, no data)',
  COLLAPSE_FILTER = 'Collapse Filter',
  GO_BACKTO_PROFILER = 'Go Back to Profiler',
  PAGINATION = 'Pagination',
  LEFT_CLICK = 'Left Click',
  RIGHT_CLICK = 'Right Click',
  HINT_HYPERLINK = 'Hint Hyperlink',
  PLATFORM = 'Platform',
  EVENT_DESCRIPTION = 'User downloads the export file',
  EXPORT_DATA = 'Export Data',
  CURRENCY = 'Currency',
  BANNER_EXPAND = 'Banner Expand',
  BANNER_COLLAPSED = 'Banner Collapsed',
  FORMAT_TOGGLE = 'Format Toggle',
  CAROUSEL = 'Carousel',
  READ_MORE = 'Read more',
  SAVED_FILTERS = 'Saved filters',
  SAVED_FILTER_OPTION = 'Saved filter options',
  SAVED_FILTER_NAVIGATION = 'Saved filters navigation',
  DELETE = 'Delete',
  SELECT_COMPETITORS = 'Select competitors',
  GO_BACKTO_STORIES = 'Go Back to Stories',
  SELECT_SHOPPERS = 'Select shoppers for comparison',
  SELECT_BRAND = 'Select Brand',
  CHART_PANEL = 'Chart Panel',
  SELECT_RETAILER = 'Select Retailer',
  SELECT_CHANNEL_VISISTED = 'Select Channel Visited',
  AI_BUTTON_CLICKED = 'AI Button Clicked',
}

export interface TrackProps {
  id: AnalyticsEvent
  message:
    | NavClickMessage
    | FiltersApplied
    | SearchClicked
    | FilterTrayButtonClick
    | FiltersAppliedDetails
    | ChartInteractions
    | PageElementClick
    | PageElementViewed
    | PageScrolled
    | PageViewed
    | TableClicked
}

interface PageProps {
  location: string
}

const Analytics = () => {
  const { page, identify, track, group } = useAnalytics()
  return {
    page,
    identify,
    track,
    group,
  }
}

export const trackNavigationEvent = (props: TrackProps) => {
  const { id, message } = props

  Analytics().track(id, message)
}
export const trackEvent = (props: TrackProps) => {
  const { id, message } = props
  Analytics().track(id, { ...message, access: TRACKING.ACCESS })
}

export const trackPageScrollEvent = (props: TrackProps) => {
  const { section, pageName, subSection } = store.getState().tracking.section.value
  const { id, message } = props
  Analytics().track(id, {
    ...message,
    access: TRACKING.ACCESS,
    pageName,
    section,
    subsection: subSection,
  })
}

export const trackPageViewedEvent = (id: string) => {
  const { section, pageName, subSection } = store.getState().tracking.section.value
  const location = store.getState().tracking.page.value
  const overview = 'overview'
  const { track } = Analytics()
  if (location?.page.includes(CIE_ALLOWED_PATH.TABLE_OF_CONTENTS)) {
    track(id, {
      access: TRACKING.ACCESS,
      pageName: CIE_ALLOWED_PATH.TABLE_OF_CONTENTS,
      section: Location.TABLE_OF_CONTENTS,
      subsection: Location.TABLE_OF_CONTENTS,
    })
  } else if (!location?.page.includes(Location.AUDIENCE_PROFILER)) {
    Analytics().track(id, {
      access: TRACKING.ACCESS,
      pageName: pageName ? pageName : overview,
      section: section ? section : overview,
      subsection: subSection ? subSection : overview,
    })
  }
}

export const trackPage = (props: PageProps) => {
  const { location } = props
  Analytics().page(location, { access: TRACKING.ACCESS })
}
